// eslint-disable-next-line no-restricted-imports
import styled from '@emotion/styled';
import type {
  AeropayBankAccount,
  CustomerReservationDetails,
} from '@iheartjane/data-access';
import { BankAccountDetails } from '@iheartjane/menu';
import {
  Banner,
  Box,
  Button,
  AlertIcon,
  Flex,
  JanePayLogoWhite,
  Typography,
  useTabletMediaQuery,
} from '@iheartjane/reefer';
import { trackError } from '@iheartjane/tracking';
import { formatCurrency } from '@iheartjane/utils';
import get from 'lodash-es/get';
import { useEffect, useRef, useState } from 'react';

import { useTipSettings } from '@jane/shared-ecomm/hooks';
import {
  trackClickedProceedToPayment,
  trackViewedPaymentSummary,
} from '@jane/shared-ecomm/tracking';
import type { DeepWriteable } from '@jane/shared-ecomm/types';
import { calculateJanePayFee } from '@jane/shared-ecomm/util';
import type { ApiRequestError, LinkSource } from '@jane/shared/data-access';
import { useUpdateCheckoutPayment } from '@jane/shared/data-access';
// eslint-disable-next-line no-restricted-imports
import type { PendingCartProduct, Store } from '@jane/shared/models';
import type { TipSetting } from '@jane/shared/types';
import { getJaneDeviceId } from '@jane/shared/util';

import { AEROPAY_REGEX, AeropayError } from '../AeropayError';
import { CheckoutReviewProductCard } from '../checkoutReviewProductCard/CheckoutReviewProductCard';
import { TippingInput } from '../tippingInput/tippingInput';
import { OrderDetails } from './OrderDetails/OrderDetails';

export interface ReviewPaymentScreenProps {
  cart: CustomerReservationDetails;
  hideJanePayBranding: boolean;
  linkSource: LinkSource;
  onConfirmOrder?: () => void;
  onSuccess: () => void;
  reviewOrderStep?: boolean;
  selectedBank?: AeropayBankAccount;
  store: Store;
}

const JanePayButton = styled(Button)(() => ({
  // TODO: Not sure why this works and theme.colors doesn't for custom colors
  backgroundColor: '--colors-primary-main',
  borderColor: '--colors-primary-main',
}));

const ButtonContainer = styled(Flex)<{ isMobile: boolean }>(({ isMobile }) => ({
  bottom: '0',
  backgroundColor: 'white',
  paddingBottom: 25,
  paddingTop: 20,
  paddingLeft: 20,
  paddingRight: 20,
  marginTop: 20,
  ...(isMobile && {
    marginLeft: -32,
    width: '100%',
    boxShadow: '0px 0px 10px rgba(0,0,0,.15)',
    position: 'fixed' as const,
  }),
  zIndex: 1000,
}));

export const ReviewPaymentScreen = ({
  cart,
  hideJanePayBranding,
  linkSource,
  onConfirmOrder,
  reviewOrderStep,
  selectedBank,
  store,
  onSuccess,
}: ReviewPaymentScreenProps) => {
  const hasTracked = useRef('');
  const jdid = getJaneDeviceId();
  const [tip, setTip] = useState(0);
  const [isExpired, setIsExpired] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | undefined>();
  const [aeropayError, setAeropayError] = useState<string | undefined>();
  const isTablet = useTabletMediaQuery({});

  useEffect(() => {
    if (hasTracked.current === '' && reviewOrderStep) {
      hasTracked.current = 'review-order';
      trackViewedPaymentSummary('initial payment screen');
    } else if (
      (hasTracked.current === '' || hasTracked.current === 'review-order') &&
      !reviewOrderStep
    ) {
      hasTracked.current = 'finalize-order';
      trackViewedPaymentSummary('final payment screen');
    }
  });

  const janePayFee = calculateJanePayFee(
    Number(cart.order_total) - Number(cart.service_fee_amount),
    {
      per_transaction:
        store.jane_pay_integration?.consumer_fee_per_transaction || '0',
      rate: store.jane_pay_integration?.consumer_fee_rate || '0',
    }
  );

  const { mutate: updateCheckoutPayment, isPending } = useUpdateCheckoutPayment(
    {
      onError: async (err: ApiRequestError) => {
        trackError('PCP: Error updating checkout payment', { error: err });
        try {
          const text = await err.response.text();
          const errorData = JSON.parse(text);
          const exception = get(errorData, 'exception', '');
          setAeropayError(exception.match(AEROPAY_REGEX));
          if (err?.response?.status === 410) {
            setIsExpired(true);
            setCheckoutError(
              'Payment link has expired. Your order has been fulfilled and is out for delivery. Please pay the driver when they arrive.'
            );
          } else {
            setCheckoutError('There was an error processing your request.');
          }
        } catch {
          setCheckoutError('There was an error processing your request.');
        }
      },
    }
  );

  const { tipOptions, tipSettings, showTip } = useTipSettings({
    reservationMode: cart.reservation_mode,
    pickupMode: cart.curbside_pickup_selected ? 'curbside' : 'pickup',
    deliveryTipSettings:
      (store?.delivery_tip_settings as DeepWriteable<TipSetting>) || undefined,
    tipSettings:
      (store.tip_settings as DeepWriteable<TipSetting>[]) || undefined,
    selectedPaymentMethod: 'jane_pay',
    tipPaymentMethods: ['jane_pay'],
  });

  const onJanePayClick = () => {
    if (reviewOrderStep && onConfirmOrder) {
      trackClickedProceedToPayment();
      onConfirmOrder();
    } else if (selectedBank?.bank_account_id) {
      updateCheckoutPayment(
        {
          aeropayBankId: Number(selectedBank.bank_account_id),
          cartUuid: cart?.uuid || '',
          currentUrl: window.location.href,
          jDeviceId: jdid,
          linkSource,
          tipAmount: tip,
        },
        {
          onSuccess: () => {
            onSuccess();
          },
        }
      );
    }
  };

  return (
    <Box pb={96}>
      <Typography variant="h1-semibold">
        Review {reviewOrderStep ? 'order' : 'payment'}
      </Typography>
      {checkoutError && (
        <Banner
          full
          mt={12}
          icon={<AlertIcon />}
          variant="error"
          label={
            aeropayError ? (
              <AeropayError code={aeropayError[1]} />
            ) : (
              checkoutError
            )
          }
        />
      )}
      {store && cart && (
        <Flex mt={24} flexDirection="column">
          <OrderDetails cart={cart} store={store} />
        </Flex>
      )}
      {!reviewOrderStep && selectedBank && (
        <Flex mb={16} mt={16} flexDirection="column">
          <Typography variant="body-semibold" mb={8}>
            Bank Used
          </Typography>
          <BankAccountDetails
            hideJanePayBranding={hideJanePayBranding}
            hidePoweredByJane={hideJanePayBranding}
            bankAccount={selectedBank}
            limitLogoSize={true}
          />
        </Flex>
      )}
      {!reviewOrderStep && showTip && (
        <>
          <Typography variant="body-semibold">Add a Tip</Typography>
          <TippingInput
            cartTotal={Number(cart.order_total) || 0}
            currentTipAmount={tip}
            isPercentage={tipSettings?.variant === 'percentage'}
            onTipChange={(tipAmount?: number) => setTip(tipAmount || 0)}
            tipOptions={tipOptions}
          />
        </>
      )}
      <Flex flexDirection="column" mt={24}>
        <Typography variant="body-semibold" mb={12}>
          Order summary
        </Typography>
        {cart.products.map((product) => {
          return (
            <CheckoutReviewProductCard
              key={`${product.id}-${product.price_id}`}
              product={product as unknown as PendingCartProduct}
              originalPrice={product.checkout_price}
              discountedPrice={product.discounted_checkout_price}
              store={store}
            />
          );
        })}
      </Flex>
      <Flex flexDirection="column" mt={24}>
        <Flex width="100%" justifyContent="space-between">
          <Typography>Subtotal</Typography>
          <Typography>{formatCurrency(cart.order_total)}</Typography>
        </Flex>
        {janePayFee > 0 && (
          <Flex width="100%" justifyContent="space-between">
            <Typography>Service fee</Typography>
            <Typography>{formatCurrency(janePayFee)}</Typography>
          </Flex>
        )}
        {!reviewOrderStep && showTip && (
          <Flex width="100%" justifyContent="space-between">
            <Typography>Tip</Typography>
            <Typography>{formatCurrency(tip)}</Typography>
          </Flex>
        )}
      </Flex>
      <ButtonContainer isMobile={!isTablet} flexDirection="column">
        <Flex width="100%" justifyContent="space-between" mb={16}>
          <Typography variant="h2-semibold" as="p">
            Estimated total
          </Typography>
          <Typography variant="h2-semibold" as="p">
            {formatCurrency(Number(cart.order_total) + tip + janePayFee)}
          </Typography>
        </Flex>
        <JanePayButton
          full
          disabled={isPending || isExpired}
          endIcon={
            hideJanePayBranding || reviewOrderStep ? (
              ''
            ) : (
              <JanePayLogoWhite
                style={{ maxWidth: 95, width: '100%' }}
                height={18}
              />
            )
          }
          loading={isPending}
          label={
            reviewOrderStep
              ? 'Proceed to payment'
              : `Pay now with${hideJanePayBranding ? ' linked account' : ''}`
          }
          onClick={onJanePayClick}
        />
      </ButtonContainer>
    </Box>
  );
};
